import { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { StockPlanningItem, ColumnPreference } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { Pagination } from '../../ui/pagination';
import StockPlanningTable from './StockPlanningTable';
import { useDirectProductToggle } from './useDirectProductToggle';
import { useStockPlanningRemark } from './useStockPlanningRemark';
import { canEditRemark } from './shortageColumns';
import ColumnManager from './ColumnManager';
import StockPlanningFilters from './StockPlanningFilters';
import StockPlanningExportMenu from './StockPlanningExportMenu';
import PrintableStockPlanning from './PrintableStockPlanning';
import {
  planningParams, printFilterSummary, useStockPlanningExport,
  type StockPlanningFiltersState,
} from './useStockPlanningExport';
import { isAllSuppliers, SUPPLIER_COLUMN } from './allSuppliers';
import CallOffWizard from '../management/CallOffWizard';
import StockStatusSubTab from '../sub-tabs/StockStatusSubTab';
import OpenPOSubTab from '../sub-tabs/OpenPOSubTab';
import SALCommittedSubTab from '../sub-tabs/SALCommittedSubTab';
import PolishFactorySubTab from '../sub-tabs/PolishFactorySubTab';
import MinMaxSubTab from '../sub-tabs/MinMaxSubTab';
import ForecastSubTab from '../sub-tabs/ForecastSubTab';
import PolandStockSubTab from '../sub-tabs/PolandStockSubTab';
import InTransitSubTab from '../sub-tabs/InTransitSubTab';
import { toast } from 'sonner';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { todayCommittedBy } from '../shared/committedByDate';
import PartSearchInput from '../shared/PartSearchInput';
import PartPrefixSelect from '../shared/PartPrefixSelect';
import { matchesPartPrefixFilter, type PartPrefixFilter } from '../shared/partPrefixFilter';

export default function StockPlanningTab() {
  const { user } = useAuth();
  const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';

  // CR-6176 / CR-1154: open the PO Lines tab pre-filtered to this part number in
  // a NEW TAB, so the user can compare stock planning and the part's PO lines
  // side by side. The PO Lines tab reads `partNumber` from the URL on mount (see
  // usePOLinesState); a fresh tab recovers the session via the CR-6282
  // BroadcastChannel handoff.
  const handlePartNumberClick = (partNumber: string) => {
    const params = new URLSearchParams({ tab: 'po_lines', partNumber });
    window.open(`/purchasing?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };
  const { config } = useCallOffConfig();
  const [items, setItems] = useState<StockPlanningItem[]>([]);
  const { formatDate } = useFormatters();
  const [columns, setColumns] = useState<ColumnPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showNewDialog, setShowNewDialog] = useState(false);
  // The chosen supplier as the dropdown names it, for the printed sheet.
  const [supplierLabel, setSupplierLabel] = useState('');
  const [filters, setFilters] = useState<StockPlanningFiltersState>({
    supplierId: '',
    openPoDays: 7,
    // CR-8191: opens on today (the backend default) instead of a blank box.
    salCommittedEndDate: todayCommittedBy(),
    factoryFilter: '',
    commodityType: '',
  });
  // CR-8193: "All call-off suppliers" — one row per part and supplier.
  const allSuppliers = isAllSuppliers(filters.supplierId);
  // Whether the rows on screen came from the combined grid. Follows the data,
  // not the filter, so the count keeps its own label while the next load runs.
  const [loadedAllSuppliers, setLoadedAllSuppliers] = useState(false);
  // CR-8613: the customer ticks Direct per part here as well as on the Parts
  // List. Priority moved to the call-off's warehouse wizard.
  const { toggleDirect, savingDirectIds } = useDirectProductToggle(setItems);
  // CR-8624: remarks per part and supplier; a customer admin types them.
  const { saveRemark, isRemarkSaving } = useStockPlanningRemark(setItems, filters.supplierId);
  // Hide, or show only, rows whose part_number or description starts with the
  // literal "part" (case-insensitive). Mirrors the supplier-side Current Stock
  // filter on the call-off stock-upload grid. Applied client-side over the
  // current page.
  const [partFilter, setPartFilter] = useState<PartPrefixFilter>('all');
  // CR-7717: part number / description search. Runs server-side so the count
  // and the pages cover every matching part, not just the loaded page.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  const fetchData = useCallback(async () => {
    // Don't fetch data until a supplier is selected
    if (!filters.supplierId) {
      setItems([]);
      setTotalCount(0);
      setLoadedAllSuppliers(false);
      return;
    }

    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        ...planningParams(filters, debouncedSearch),
        page,
        page_size: pageSize,
      };

      const [dataRes, colRes] = await Promise.all([
        callOffAPI.getStockPlanning(params),
        callOffAPI.getColumns(),
      ]);

      setItems(dataRes.data.items);
      setTotalCount(dataRes.data.total_count);
      setLoadedAllSuppliers(isAllSuppliers(filters.supplierId));
      setColumns(colRes.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load stock planning data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleColumn = async (columnId: string) => {
    try {
      await callOffAPI.toggleColumn(columnId);
      const res = await callOffAPI.getColumns();
      setColumns(res.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle column';
      toast.error(message);
    }
  };

  const handleSortColumns = async (updatedColumns: { column_id: string; sort_order: number }[]) => {
    try {
      const res = await callOffAPI.sortColumns(updatedColumns);
      setColumns(res.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sort columns';
      toast.error(message);
    }
  };

  const {
    exporting, printing, printItems, handleExport, handleExportSupplierStock, handlePrint,
  } = useStockPlanningExport({ filters, columns, search: debouncedSearch, partFilter });

  const [subTab, setSubTab] = useState('stock_status');
  // Selected part filters sub-tab data; will be set when a row is clicked in the grid
  const [selectedPart] = useState<string | undefined>(undefined);

  // CR-8193: the combined grid leads with a Supplier column. It is not one of
  // the saved columns, so the Columns menu never lists it.
  const visibleColumns = [
    ...(allSuppliers ? [SUPPLIER_COLUMN] : []),
    ...columns.filter((c) => c.is_visible).sort((a, b) => a.sort_order - b.sort_order),
  ];
  const totalPages = Math.ceil(totalCount / pageSize);
  // A row is a part on one supplier's grid, a part-and-supplier on the combined one.
  const rowLabel = loadedAllSuppliers ? 'rows' : 'parts';

  const visibleItems = items.filter(
    (it) => matchesPartPrefixFilter(partFilter, it.part_number, it.description),
  );

  return (
    <div className="space-y-4">
      {/* CR-6194: the filter panel is now a sectioned card, so it sits as its
          own full-width block above the toolbar rather than inline beside the
          Parts select — a card can't share a `flex items-end` row with a bare
          control without the baselines going out. */}
      <StockPlanningFilters
        filters={filters}
        onFiltersChange={(f) => { setFilters(f); setPage(1); }}
        onSupplierLabelChange={setSupplierLabel}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Search</Label>
            <PartSearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              className="w-72"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Parts</Label>
            <PartPrefixSelect
              value={partFilter}
              onChange={setPartFilter}
              className="w-52 px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StockPlanningExportMenu
            hasSupplier={Boolean(filters.supplierId)}
            allSuppliers={allSuppliers}
            exporting={exporting}
            printing={printing}
            onExport={handleExport}
            onExportSupplierStock={handleExportSupplierStock}
            onPrint={handlePrint}
          />
          <ColumnManager
            columns={columns}
            onToggle={handleToggleColumn}
            onSort={handleSortColumns}
          />
          {isCustomer && (
            // CR-8193: a call-off is for one supplier, so the wizard is not
            // offered from the combined grid. The span carries the tooltip,
            // since a disabled button raises no hover events.
            <span title={allSuppliers ? 'Pick one supplier to create a call-off' : undefined}>
              <Button className="gap-2" onClick={() => setShowNewDialog(true)} disabled={allSuppliers}>
                <Plus className="h-4 w-4" />
                New Call Off
              </Button>
            </span>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Stock Planning Grid
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({partFilter !== 'all' ? `${visibleItems.length} of ${totalCount}` : totalCount} {rowLabel})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filters.supplierId && (
            <div className="mb-3 border-b pb-3">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                itemLabel={rowLabel}
              />
            </div>
          )}
          {!filters.supplierId ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Select a supplier to view stock planning data
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : debouncedSearch && items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No parts match &ldquo;{debouncedSearch}&rdquo;.
            </div>
          ) : partFilter !== 'all' && visibleItems.length === 0 && items.length > 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              All {items.length} rows on this page were hidden by the active filter.
            </div>
          ) : (
            <StockPlanningTable
              items={visibleItems}
              columns={visibleColumns}
              onPartNumberClick={handlePartNumberClick}
              onToggleDirect={isCustomer ? toggleDirect : undefined}
              savingDirectIds={savingDirectIds}
              onSaveRemark={canEditRemark(user) ? saveRemark : undefined}
              isRemarkSaving={isRemarkSaving}
            />
          )}

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            itemLabel={rowLabel}
          />
        </CardContent>
      </Card>

      {printItems && (
        <PrintableStockPlanning
          items={printItems}
          columns={visibleColumns}
          supplierLabel={supplierLabel}
          filterSummary={printFilterSummary(filters, debouncedSearch, partFilter)}
          formatDate={formatDate}
          generatedBy={user?.full_name || user?.email || null}
        />
      )}

      {/* Sub-tabs for detailed stock planning views */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Detail Views</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={subTab} onValueChange={setSubTab}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="stock_status" className="text-xs">Stock Status</TabsTrigger>
              <TabsTrigger value="open_po" className="text-xs">Open PO</TabsTrigger>
              <TabsTrigger value="sal_committed" className="text-xs">SAL Committed</TabsTrigger>
              {config.polish_factory_subtab_enabled && (
                <TabsTrigger value="polish_factory" className="text-xs">Polish Factory</TabsTrigger>
              )}
              <TabsTrigger value="min_max" className="text-xs">Min/Max</TabsTrigger>
              <TabsTrigger value="forecast" className="text-xs">Forecast</TabsTrigger>
              <TabsTrigger value="poland_stock" className="text-xs">Poland Stock</TabsTrigger>
              <TabsTrigger value="in_transit" className="text-xs">In Transit</TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <TabsContent value="stock_status"><StockStatusSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="open_po"><OpenPOSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="sal_committed"><SALCommittedSubTab partNumber={selectedPart} /></TabsContent>
              {config.polish_factory_subtab_enabled && (
                <TabsContent value="polish_factory"><PolishFactorySubTab partNumber={selectedPart} /></TabsContent>
              )}
              <TabsContent value="min_max"><MinMaxSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="forecast"><ForecastSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="poland_stock"><PolandStockSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="in_transit"><InTransitSubTab partNumber={selectedPart} /></TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      <CallOffWizard
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={fetchData}
        initialSupplierId={filters.supplierId && !allSuppliers ? Number(filters.supplierId) : null}
        initialOpenPoDays={filters.openPoDays}
        initialCommittedByDate={filters.salCommittedEndDate}
      />
    </div>
  );
}
